import React from 'react';
import {
  ApButtonSecondary,
  ApSimpleDropdown,
} from '@alixpartners/ui-components';

import {
  CONTRIBUTIONS,
  DISPLAY_BY,
  DISPLAY_BY_OPTIONS,
  EXPORT_OPTIONS,
  getSpendContributionOptions,
  PERMISSIONS,
} from 'utils/constants';
import { IDisplayByType, ISelectOption } from 'utils/types';
import QueryParams from 'context/QueryParams';
import {
  Container,
  Divider,
  Top,
  Bottom,
  BreadcrumbsContainer,
  SpaceBetweenDropdowns,
  Wrapper,
  CheckboxWrapper,
  StyledCheckbox,
} from './ChartOptions.styles';
import { Breadcrumbs } from '../Breadcrumbs';
import { ApiContext } from 'context/ApiContext';
import { IChartOptionsConfig } from '../types';
import { PERCENTAGE_OF_COST } from '../utils';

const BREADCRUMBS_MAP = {
  '': 'Product spend',
  [CONTRIBUTIONS.THIRD_PARTY]: 'Product Third party spend',
  [CONTRIBUTIONS.PERSONNEL]: 'Product Personnel spend',
};

export const ChartOptionsView = ({
  chartOptionsConfig,
  onExport,
  permissions,
  setChartOptionsConfig,
}: {
  chartOptionsConfig: IChartOptionsConfig;
  setChartOptionsConfig: (c: IChartOptionsConfig) => void;
  permissions: string[];
  onExport: () => {};
}) => {
  const {
    contributions,
    displayBy,
    level1,
    level2,
    level3,
    revenueCheckbox,
    costCheckbox,
    costAsPercentageCheckbox,
  } = chartOptionsConfig;
  const [isHide, setIsHide] = React.useState<boolean>(false);
  const isPersonnelDisabled = !permissions.includes(
    // Permission for personnel
    PERMISSIONS.PRODUCTS_CHARTS_PERSONNEL
  );
  const isOnlyCheckboxChecked =
    [revenueCheckbox, costCheckbox, costAsPercentageCheckbox].filter((el) => el)
      .length === 1;
  const handleCheckboxChange = (e: any) => {
    const name = e.target.name;
    const checked = e.target.checked;
    if (checked || !isOnlyCheckboxChecked) {
      setChartOptionsConfig({
        ...chartOptionsConfig,
        [name]: checked,
      });
    }
  };
  const spendContributionsOptions =
    getSpendContributionOptions(isPersonnelDisabled);
  const isPersonnel = contributions === CONTRIBUTIONS.PERSONNEL;
  let selectedContribution =
    spendContributionsOptions[isPersonnelDisabled ? 1 : 0];
  if (contributions) {
    if (contributions.length === 2) {
      selectedContribution = spendContributionsOptions[0];
    } else {
      selectedContribution =
        isPersonnel && !isPersonnelDisabled
          ? spendContributionsOptions[2]
          : spendContributionsOptions[1];
    }
  }
  let breadcrumbs = [BREADCRUMBS_MAP[selectedContribution.value]];
  if (level1) {
    breadcrumbs.push(level1);
  }
  if (level2) {
    breadcrumbs.push(level2);
  }
  if (level3) {
    breadcrumbs.push(level3);
  }
  let displayByOption;
  if (displayBy) {
    displayByOption = {
      label: displayBy,
      value: displayBy,
    };
  }

  return (
    <Container>
      <Top>
        <BreadcrumbsContainer>
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onClick={(id) => {
              if (id === 0) {
                setChartOptionsConfig({
                  ...chartOptionsConfig,
                  level1: undefined,
                  level2: undefined,
                  level3: undefined,
                });
              }
              if (id === 1) {
                setChartOptionsConfig({
                  ...chartOptionsConfig,
                  level2: undefined,
                  level3: undefined,
                });
              }
              if (id === 2) {
                setChartOptionsConfig({
                  ...chartOptionsConfig,
                  level3: undefined,
                });
              }
            }}
          />
        </BreadcrumbsContainer>
        <ApButtonSecondary iconName="tune" onClick={() => setIsHide(!isHide)}>
          {isHide ? 'Show ' : 'Hide '}options
        </ApButtonSecondary>
      </Top>
      <Bottom isHide={isHide}>
        <Wrapper>
          <ApSimpleDropdown
            onChange={(option) => {
              const { value } = option as ISelectOption;
              let newDisplayBy = displayBy;
              if (value !== CONTRIBUTIONS.PERSONNEL) {
                newDisplayBy = IDisplayByType.COST;
              }
              setChartOptionsConfig({
                ...chartOptionsConfig,
                contributions: value,
                displayBy: newDisplayBy,
              });
            }}
            options={spendContributionsOptions}
            selected={selectedContribution}
          />

          <SpaceBetweenDropdowns />

          <ApSimpleDropdown
            onChange={(option) => {
              const { value } = option as ISelectOption;
              setChartOptionsConfig({
                ...chartOptionsConfig,
                displayBy: value as IDisplayByType,
              });
            }}
            options={[DISPLAY_BY_OPTIONS[0], DISPLAY_BY_OPTIONS[2]].map(
              (option) => ({
                ...option,
                disabled: option.value === DISPLAY_BY.HEADCOUNT && !isPersonnel,
              })
            )}
            selected={displayByOption}
          />

          <Divider />
          {!level1 && displayBy !== DISPLAY_BY.HEADCOUNT && (
            <React.Fragment>
              <CheckboxWrapper>
                <StyledCheckbox
                  id="revenueCheckbox"
                  name="revenueCheckbox"
                  disabled={isOnlyCheckboxChecked && revenueCheckbox}
                  checked={revenueCheckbox}
                  onChange={handleCheckboxChange}
                >
                  Revenue
                </StyledCheckbox>
              </CheckboxWrapper>
              <CheckboxWrapper>
                <StyledCheckbox
                  id="costCheckbox"
                  name="costCheckbox"
                  disabled={isOnlyCheckboxChecked && costCheckbox}
                  checked={costCheckbox}
                  onChange={handleCheckboxChange}
                >
                  Cost
                </StyledCheckbox>
              </CheckboxWrapper>
              <CheckboxWrapper>
                <StyledCheckbox
                  id="costAsPercentageCheckbox"
                  name="costAsPercentageCheckbox"
                  disabled={isOnlyCheckboxChecked && costAsPercentageCheckbox}
                  checked={costAsPercentageCheckbox}
                  onChange={handleCheckboxChange}
                >
                  {PERCENTAGE_OF_COST}
                </StyledCheckbox>
              </CheckboxWrapper>
            </React.Fragment>
          )}
        </Wrapper>
        <ApSimpleDropdown
          iconName="outline_save_alt"
          onChange={onExport}
          options={EXPORT_OPTIONS}
          label="Export"
        />
      </Bottom>
    </Container>
  );
};

export default (props: any) => (
  <ApiContext.Consumer>
    {({ permissions }) => (
      <QueryParams>
        {({ params, setParams }) => (
          <ChartOptionsView
            {...props}
            params={params}
            permissions={permissions}
            setParams={setParams}
          />
        )}
      </QueryParams>
    )}
  </ApiContext.Consumer>
);
